import { type Draft } from 'immer';
import { type PartialDeep } from 'type-fest';

import { type EngineStateBase } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElementFrom,
  type CanvasElementId,
  findByRefIds,
  setWrapperRefMutably,
  WrapperActionButton,
  type WrapperActionButtonRefId,
  type WrapperRefIds,
  type WrapperRefs,
} from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';
import { type PropertiesConfigInputContainerItemActionWithIdModel } from '@blaze-shared/elements-domain';
import { deepClone, deepmergeExceptArrays } from '@blaze-shared/shared-util';
import { fromStyles } from '@blaze-shared/style-state-feature';
import { type StyleSheet, type StyleSheetTarget } from '@blaze-shared/theming-domain';

import { type CanvasElementDefinitionInternal } from '../../../canvas-elements/element-definition';
import {
  hasProps,
  hasStylesConfig,
  hasWrapperRefs,
} from '../../../canvas-elements/element-model/element-props-guards';
import { isVariantOfFormContainer } from '../../../canvas-elements/element-model/element-type-guards';
import { getWrapperRefs } from '../../../canvas-elements/element-model/wrapper-refs';
import { type ButtonPropsModel } from '../../../elements/button/button.element';
import { setStyleSheetToElementMutably } from '../../../services/styles';
import { type CreateCanvasActionButtonModel } from '../../../utils/elements/generation.utils';
import { getPathLastInSlot } from '../../../utils/layout/helpers';
import { placeElementMutably } from '../../../utils/layout/place-element';
import { removeElementMutably } from '../../../utils/layout/remove-element';

/** Creates or updates the form actions buttons */
export function regenerateFormActionsMutably(
  draft: Draft<EngineStateBase>,
  buttonDefinition: CanvasElementDefinitionInternal<'button'>,
  containerId: CanvasElementId,
  styleSheets: StyleSheet[],
  actionsLayoutChanged: boolean,
): void {
  const container = draft.entities[containerId];
  if (!container || !hasProps(container) || !isVariantOfFormContainer(container)) {
    throw new Error('Actions container element not found');
  }
  if (!hasWrapperRefs(container)) {
    container.props = applyPropsPatch(container.props, {
      wrapper: { refs: {} },
    }) as typeof container.props;
    if (!hasWrapperRefs(container)) {
      throw new Error('Wrapper refs missing error');
    }
  }
  const refs = getWrapperRefs(container);
  const model = container.props.content.formActions || [];
  const pregenerationData = model.map((actionConfig) => ({
    refIds: WrapperActionButton.getRefIds(actionConfig.id),
    actionConfig,
  }));
  const pregenerationRefIds = pregenerationData.flatMap((p) => p.refIds);

  // Remove field elements which are no longer included
  Object.entries(refs).forEach(([refId, refElementId]) => {
    if (WrapperActionButton.isRefId(refId) && !pregenerationRefIds.some((rId) => rId === refId)) {
      delete refs[refId];
      removeElementMutably(draft, refElementId);
    }
  });

  const elementsCreationData = prepareActionButtonsElements(pregenerationData);

  for (const fieldData of elementsCreationData) {
    addOrUpdateActionButtonsMutably(
      draft,
      buttonDefinition,
      styleSheets,
      container,
      refs,
      fieldData,
      actionsLayoutChanged,
    );
  }
}

type ActionButtonCreationData = CreateCanvasActionButtonModel & {
  styleSheetTarget?: StyleSheetTarget;
};

function prepareActionButtonsElements(
  pregenerationData: {
    refIds: WrapperRefIds<WrapperActionButtonRefId>;
    actionConfig: PropertiesConfigInputContainerItemActionWithIdModel;
  }[],
): ActionButtonCreationData[] {
  const fieldElements: ActionButtonCreationData[] = [];

  for (const { refIds, actionConfig } of pregenerationData) {
    const updateProps: PartialDeep<ButtonPropsModel> = {
      content: {
        button: {
          label: actionConfig.showInlineLabel ? actionConfig.label : '',
          icon: actionConfig.icon,
          iconPosition: actionConfig.iconPosition,
        },
        action: {
          ...actionConfig.action,
        },
      },
    };

    const styleSheetTarget =
      actionConfig.styleSheetTarget ??
      (actionConfig.styleSheetId ? { id: actionConfig.styleSheetId } : undefined);

    fieldElements.push({ refIds, updateProps, styleSheetTarget });
  }

  return fieldElements;
}

function addOrUpdateActionButtonsMutably(
  draft: Draft<EngineStateBase>,
  buttonDefinition: CanvasElementDefinitionInternal<'button'>,
  styleSheets: StyleSheet[],
  container: CanvasElementFrom<'formContainer' | 'formDetails'>,
  refs: WrapperRefs,
  fieldData: ActionButtonCreationData,
  actionsLayoutChanged: boolean,
): void {
  let elementId: CanvasElementId | undefined = findByRefIds(refs, fieldData.refIds);
  const isNewElement = !elementId || !draft.entities[elementId];
  if (isNewElement) {
    // If element missing - create element
    const targetPath = getPathLastInSlot(draft, container.id, '0');
    const result = placeElementMutably(draft, {
      elementDefinition: buttonDefinition,
      // Cloned so sibling buttons created in one pass don't share nested initial props objects
      initProps: deepClone(buttonDefinition.initial),
      atPath: targetPath,
      style: { styleSheets, useDefault: true },
    });
    if (!result?.elementId) {
      throw new Error(`Action button creation error`);
    }

    // Store the reference
    elementId = result.elementId;
    setWrapperRefMutably(refs, fieldData.refIds, elementId);
  }

  if (!elementId) {
    throw new Error('Action button error');
  }
  const element = draft.entities[elementId];
  if (!element) {
    throw new Error('Action button error');
  }

  // Update props
  if (hasProps(element) && fieldData.updateProps) {
    element.props = deepmergeExceptArrays(element.props as any, fieldData.updateProps as any);
  }

  applyActionButtonStyleSheetMutably(
    draft,
    buttonDefinition,
    styleSheets,
    elementId,
    fieldData.styleSheetTarget,
  );

  // Side-by-side buttons flow inline (fit-content), stacked buttons take the full form width
  // Note: In the future most likely we will store actions in container that will have more
  // control over the layout, so this logic will be moved there
  if (isNewElement || actionsLayoutChanged) {
    const sideBySide = container.props.content.options?.actionsLayout === 'sideBySide';
    element.width = sideBySide
      ? { ...element.width, mode: 'value', fixed: false }
      : { ...element.width, mode: 'full' };
  }
}

/** Applies the action's `Style (optional)` sheet to the generated button element */
function applyActionButtonStyleSheetMutably(
  draft: Draft<EngineStateBase>,
  buttonDefinition: CanvasElementDefinitionInternal<'button'>,
  styleSheets: StyleSheet[],
  elementId: CanvasElementId,
  target: StyleSheetTarget | undefined,
): void {
  const element = draft.entities[elementId];
  if (!hasProps(element)) {
    return;
  }

  const styleId = target?.id;
  const styleName = styleId ? undefined : target?.name;
  if (!styleId && !styleName) {
    return;
  }

  // Skip when unchanged - reassigning can wipe custom style overrides
  const current = hasStylesConfig(element) ? element.props.styleConfig : undefined;
  if (
    (styleId && current?.styleSheetId === styleId) ||
    (styleName && current?.styleName === styleName)
  ) {
    return;
  }

  const styleSheet = fromStyles.findMatchingStyleSheet({
    id: styleId,
    name: styleName,
    elementType: buttonDefinition.type,
    sheets: styleSheets,
  });
  if (!styleSheet) {
    return;
  }

  setStyleSheetToElementMutably({
    entities: draft.entities,
    elementId,
    styleSheet,
    elementDefinition: buttonDefinition,
  });
}
